import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import webhookQueueService from "../../services/WebhookQueueService";
import { ClaimsContext } from "../../services/ClaimsService";
import { FeatureType } from "../../constants/FeatureToggles";
import "./QueuedOperationsPanel.css";

const STATUS_ICONS = {
  pending: "⏳",
  processing: "🔄",
  waitingforinput: "❓",
  completed: "✅",
  failed: "❌",
};

const STATUS_LABELS = { 
  pending: "Pending",
  processing: "Processing",
  waitingforinput: "Awaiting Confirmation",
  completed: "Completed",
  failed: "Failed",
};

function formatTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function timeAgo(isoString) {
  if (!isoString) return "";
  const seconds = Math.floor((new Date() - new Date(isoString)) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function getErrorMessage(error) {
  if (!error) return null;
  if (typeof error === "string") return error;
  return (
    error.message ||
    error.errorMessage ||
    error.data?.errorMessage ||
    error.data?.message ||
    error.statusText ||
    JSON.stringify(error)
  );
}

function getReportUrl(op) {
  if (op.status !== "completed") return null;
  const result = op.result;
  if (result?._reportUrl) return result._reportUrl;
  const reportId =
    result?.Data?.ReportId ||
    result?.data?.reportId ||
    result?.data?.ReportId;
  if (reportId) {
    return `${window.location.origin}/reports/${reportId}`;
  }
  return null;
}

const QueuedOperationsPanel = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("current"); // current | history
  const [state, setState] = useState(webhookQueueService.getState());
  const [, setTick] = useState(0);
  const prevQueueLengthRef = useRef(state.queue.length);

  const { userHasFeature, userFeaturesAreLoaded } = useContext(ClaimsContext);
  const showWebhookPanel = useMemo(() => userHasFeature(FeatureType.ViewWebhookPanel), [userHasFeature]);

  useEffect(() => {
    const unsubscribe = webhookQueueService.subscribe((newState) => {
      setState(newState);

      // Auto-expand when a new operation is added to the queue
      if (newState.queue.length > prevQueueLengthRef.current) {
        setIsExpanded(true);
        setActiveTab("current");
      }
      prevQueueLengthRef.current = newState.queue.length;
    });
    return () => unsubscribe();
  }, []);

  // Update time-ago displays
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleCancel = useCallback((operationId) => {
    webhookQueueService.cancelOperation(operationId);
  }, []);

  const handleClearHistory = useCallback(() => {
    webhookQueueService.clearHistory();
  }, []);

  const handlePromptResponse = useCallback(async (operationId, confirmed) => {
    await webhookQueueService.respondToPrompt(operationId, confirmed);
  }, []);

  const pendingCount = state.queue.filter(
    (op) => op.status === "pending" || op.status === "processing" || op.status === "waitingforinput"
  ).length;

  const totalCount = state.queue.length + state.completedOps.length;

  if (!showWebhookPanel) {
    return null;
  }

  return (
    <div className={`queued-operations-panel ${isExpanded ? "expanded" : "collapsed"}`}>
      {/* Tab / Toggle Bar */}
      <div className="queued-operations-tab" onClick={handleToggle}>
        <div className="tab-content">
          <span className="tab-label">Queued Operations</span>
          {pendingCount > 0 && (
            <span className="tab-badge pulse">{pendingCount}</span>
          )}
          {pendingCount === 0 && totalCount > 0 && (
            <span className="tab-badge">{totalCount}</span>
          )}
          <span className={`tab-chevron ${isExpanded ? "up" : "down"}`}>
            ▲
          </span>
        </div>
      </div>

      {/* Expanded Panel */}
      {isExpanded && (
        <div className="queued-operations-body">
          {/* Sub-tabs */}
          <div className="queued-operations-subtabs">
            <button
              className={`subtab ${activeTab === "current" ? "active" : ""}`}
              onClick={() => setActiveTab("current")}
            >
              Current ({state.queue.length})
            </button>
            <button
              className={`subtab ${activeTab === "history" ? "active" : ""}`}
              onClick={() => setActiveTab("history")}
            >
              History ({state.completedOps.length})
            </button>
          </div>

          {/* Content */}
          <div className="queued-operations-list">
            {activeTab === "current" && (
              <>
                {state.queue.length === 0 ? (
                  <div className="queue-empty">
                    <span className="empty-icon">📭</span>
                    <p>No active operations</p>
                  </div>
                ) : (
                  state.queue.map((op) => (
                    <div key={op.id} className={`queue-item status-${op.status}`}>
                      <div className="queue-item-header">
                        <span className="queue-item-icon">
                          {STATUS_ICONS[op.status]}
                        </span>
                        <span className="queue-item-label" title={op.label}>
                          {op.label}
                        </span>
                        <span className="queue-item-time">
                          {timeAgo(op.createdAt)}
                        </span>
                      </div>
                      {(op.progressMessage || op.description) && (
                        <div className="queue-item-description">
                          {op.status === "processing" && op.progressMessage
                            ? op.progressMessage
                            : op.description}
                        </div>
                      )}

                      {op.status === "processing" && (
                        <div className="queue-item-progress">
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${op.progress || 0}%` }}
                            />
                          </div>
                          <span className="progress-text">
                            {op.progress || 0}%
                          </span>
                        </div>
                      )}
                      {op.error && (
                        <div className="queue-item-error">
                          ⚠ {getErrorMessage(op.error)}
                        </div>
                      )}
                      {getReportUrl(op) && (
                        <div className="queue-item-link">
                          <a
                            href={getReportUrl(op)}
                            className="view-report-link"
                            title="View Report"
                          >
                            View Report
                          </a>
                        </div>
                      )}
                      <div className="queue-item-footer">
                        <span className={`status-label ${op.status}`}>
                          {STATUS_LABELS[op.status]}
                        </span>
                        {(op.status === "pending" ||
                          op.status === "processing" ||
                          op.status === "waitingforinput") && (
                          <button
                            className="cancel-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancel(op.id);
                            }}
                            title="Cancel operation"
                          >
                            ✕ Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

            {activeTab === "history" && (
              <>
                {state.completedOps.length === 0 ? (
                  <div className="queue-empty">
                    <span className="empty-icon">📄</span>
                    <p>No past operations</p>
                  </div>
                ) : (
                  <>
                    <div className="history-actions">
                      <button
                        className="clear-history-btn"
                        onClick={handleClearHistory}
                      >
                        🗑 Clear History
                      </button>
                    </div>
                    {state.completedOps.map((op) => (
                      <div
                        key={op.id}
                        className={`queue-item status-${op.status}`}
                      >
                        <div className="queue-item-header">
                          <span className="queue-item-icon">
                            {STATUS_ICONS[op.status]}
                          </span>
                          <span className="queue-item-label" title={op.label}>
                            {op.label}
                          </span>
                          <span className="queue-item-time">
                            {formatTime(op.updatedAt)}
                          </span>
                        </div>
                        {op.description && (
                          <div className="queue-item-description">
                            {op.description}
                          </div>
                        )}
                        {op.error && (
                          <div className="queue-item-error">
                            ⚠ {getErrorMessage(op.error)}
                          </div>
                        )}
                        {getReportUrl(op) && (
                          <div className="queue-item-link">
                            <a
                              href={getReportUrl(op)}
                              className="view-report-link"
                              title="View Report"
                            >
                              View Report
                            </a>
                          </div>
                        )}
                        <div className="queue-item-footer">
                          <span className={`status-label ${op.status}`}>
                            {STATUS_LABELS[op.status]}
                          </span>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QueuedOperationsPanel;